import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { api } from '@/services/api';
import { RefreshCw, Activity, Cpu, Clock, User, CheckCircle, XCircle } from 'lucide-react';
import PipelineVisualization from './ai/PipelineVisualization';
import ProviderStatusCards from './ai/ProviderStatusCards';
import RecentTaskList, { TaskHistoryItem } from './ai/RecentTaskList';
import TaskDetailModal from './ai/TaskDetailModal';
import TimelineChart from './ai/TimelineChart';

interface SystemOverview {
  queue: { low: number; mid: number; high: number; total: number };
  workers: { worker_count: number; total_tasks_processed: number };
  recent_1h: { completed: number; failed: number };
  timestamp: string;
}

interface WorkerInfo {
  worker_id: number;
  running: boolean;
  tasks_processed: number;
}

interface WorkersStatus {
  worker_count: number;
  workers: WorkerInfo[];
  total_tasks_processed: number;
  status: string;
}

interface TaskStatistics {
  period_hours: number;
  user_statistics: Array<{ user_id: number; email: string; username: string; task_count: number }>;
  task_type_statistics: Record<string, number>;
  status_statistics: Record<string, number>;
  priority_statistics: Record<string, number>;
}

interface ProviderInfo {
  enabled: boolean;
  healthy: boolean;
  models: Array<{ id: string; default: boolean }>;
  timeout?: number;
}

interface TimelineEntry {
  date: string;
  hour: number;
  total: number;
  completed: number;
  failed: number;
}

const AIMonitoring: React.FC = () => {
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [workersStatus, setWorkersStatus] = useState<WorkersStatus | null>(null);
  const [taskStats, setTaskStats] = useState<TaskStatistics | null>(null);
  const [providers, setProviders] = useState<Record<string, ProviderInfo>>({});
  const [recentTasks, setRecentTasks] = useState<TaskHistoryItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [taskDetail, setTaskDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [statsPeriod, setStatsPeriod] = useState(24);
  const [taskOffset, setTaskOffset] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setError(null);

      const [overviewRes, workersRes, statsRes, providersRes, tasksRes, timelineRes] =
        await Promise.all([
          api.get('/admin/ai/system/overview'),
          api.get('/admin/ai/workers/status'),
          api.get('/admin/ai/tasks/statistics', { params: { hours: statsPeriod } }),
          api.get('/admin/ai/providers/status').catch(() => ({ data: { providers: {} } })),
          api.get('/admin/ai/tasks/recent', { params: { limit: 50, offset: 0 } }).catch(() => ({ data: { tasks: [] } })),
          api.get('/admin/ai/stats/timeline', { params: { hours: 24 } }).catch(() => ({ data: { timeline: [] } })),
        ]);

      setOverview(overviewRes.data);
      setWorkersStatus(workersRes.data);
      setTaskStats(statsRes.data);
      setProviders(providersRes.data.providers || {});
      setRecentTasks(tasksRes.data.tasks || []);
      setTimeline(timelineRes.data.timeline || []);
      setTaskOffset(0);
      setLoading(false);
    } catch (err: any) {
      console.error('AI 모니터링 데이터 로드 실패:', err);
      setError(err.response?.data?.detail || err.message || '데이터를 불러오는데 실패했습니다');
      setLoading(false);
    }
  }, [statsPeriod]);

  const loadMoreTasks = async () => {
    try {
      const newOffset = taskOffset + 50;
      const res = await api.get('/admin/ai/tasks/recent', { params: { limit: 50, offset: newOffset } });
      const newTasks = res.data.tasks || [];
      setRecentTasks((prev) => [...prev, ...newTasks]);
      setTaskOffset(newOffset);
    } catch (err) {
      console.error('추가 작업 로드 실패:', err);
    }
  };

  const openTaskDetail = async (taskId: string) => {
    try {
      const res = await api.get(`/admin/ai/tasks/${taskId}`);
      setTaskDetail(res.data);
      setSelectedTaskId(taskId);
    } catch (err) {
      console.error('작업 상세 로드 실패:', err);
    }
  };

  useEffect(() => {
    fetchData();

    if (autoRefresh) {
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchData]);

  if (loading && !overview) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600">시스템 모니터링 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">AI 시스템 작업 모니터링</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            자동 갱신 (5초)
          </label>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            새로고침
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 시스템 개요 카드 */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">대기 큐</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.queue.total}</div>
              <div className="mt-4 space-y-2">
                {(['high', 'mid', 'low'] as const).map((level) => (
                  <div key={level} className="flex items-center justify-between text-sm">
                    <Badge variant={level === 'high' ? 'error' : level === 'mid' ? 'warning' : 'default'} className="text-xs">
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Badge>
                    <span className="font-medium">{overview.queue[level]}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">워커</CardTitle>
              <Cpu className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.workers.worker_count}</div>
              <p className="text-xs text-muted-foreground mt-1">활성 워커 수</p>
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">총 처리</span>
                  <span className="font-medium">{overview.workers.total_tasks_processed}건</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">최근 1시간</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-muted-foreground">완료</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{overview.recent_1h.completed}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-muted-foreground">실패</span>
                  </div>
                  <span className="text-lg font-bold text-red-600">{overview.recent_1h.failed}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 파이프라인 시각화 */}
      {overview && (
        <PipelineVisualization
          queueTotal={overview.queue.total}
          queueHigh={overview.queue.high}
          queueMid={overview.queue.mid}
          queueLow={overview.queue.low}
          workerCount={overview.workers.worker_count}
          totalProcessed={overview.workers.total_tasks_processed}
          providers={providers}
        />
      )}

      {/* Provider 상태 카드 */}
      <ProviderStatusCards providers={providers} />

      {/* 시간대별 추이 차트 */}
      <TimelineChart data={timeline} />

      {/* 워커 상태 */}
      {workersStatus && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>워커 상태</CardTitle>
              <Badge variant={workersStatus.status === 'running' ? 'success' : 'error'}>
                {workersStatus.status === 'running' ? '실행 중' : '중지됨'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {workersStatus.workers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {workersStatus.workers.map((worker) => (
                  <div key={worker.worker_id} className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">워커 #{worker.worker_id}</span>
                      <Badge variant={worker.running ? 'success' : 'default'} className="text-xs">
                        {worker.running ? '실행 중' : '대기'}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      처리 완료: <span className="font-medium text-gray-900">{worker.tasks_processed}건</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">워커가 실행되지 않았습니다.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* 작업 통계 */}
      {taskStats && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>작업 통계</CardTitle>
              <Select value={statsPeriod.toString()} onValueChange={(v) => setStatsPeriod(Number(v))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">최근 1시간</SelectItem>
                  <SelectItem value="6">최근 6시간</SelectItem>
                  <SelectItem value="24">최근 24시간</SelectItem>
                  <SelectItem value="168">최근 7일</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* 처리 상태 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">처리 상태</h3>
                <div className="space-y-2">
                  {['completed', 'failed'].map((key) => {
                    const total = (taskStats.status_statistics.completed || 0) + (taskStats.status_statistics.failed || 0);
                    const count = taskStats.status_statistics[key] || 0;
                    return (
                      <div key={key} className="flex items-center gap-3 text-sm">
                        <span className="text-gray-600 w-12">{key === 'completed' ? '완료' : '실패'}</span>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${key === 'completed' ? 'bg-green-500' : 'bg-red-500'} transition-all`}
                            style={{ width: `${Math.max(2, (count / (total || 1)) * 100)}%` }}
                          />
                        </div>
                        <span className="font-medium w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 우선순위 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">우선순위</h3>
                <div className="space-y-2">
                  {Object.entries(taskStats.priority_statistics).map(([priority, count]) => {
                    const total = Object.values(taskStats.priority_statistics).reduce((a, b) => a + b, 0);
                    const colorClass = priority === 'high' ? 'bg-red-500' : priority === 'mid' ? 'bg-yellow-500' : 'bg-gray-500';
                    return (
                      <div key={priority} className="flex items-center gap-3 text-sm">
                        <span className="text-gray-600 capitalize w-12">{priority}</span>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${colorClass} transition-all`} style={{ width: `${Math.max(2, (count / (total || 1)) * 100)}%` }} />
                        </div>
                        <span className="font-medium w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 작업 유형 */}
              {Object.keys(taskStats.task_type_statistics).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">작업 유형</h3>
                  <div className="space-y-2">
                    {Object.entries(taskStats.task_type_statistics).map(([type, count], index) => {
                      const total = Object.values(taskStats.task_type_statistics).reduce((a, b) => a + b, 0);
                      const colors = ['bg-blue-500', 'bg-purple-500', 'bg-indigo-500', 'bg-pink-500'];
                      return (
                        <div key={type} className="flex items-center gap-3 text-sm">
                          <span className="text-gray-600 truncate flex-shrink-0" style={{ width: '120px' }}>{type}</span>
                          <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full ${colors[index % colors.length]} transition-all`} style={{ width: `${Math.max(2, (count / (total || 1)) * 100)}%` }} />
                          </div>
                          <span className="font-medium w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 사용자별 통계 */}
      {taskStats && taskStats.user_statistics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              사용자별 요청 TOP 10
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {taskStats.user_statistics.map((user, index) => (
                <div key={user.user_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{user.username}</div>
                    <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  </div>
                  <Badge variant="default">{user.task_count}건</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 최근 작업 목록 */}
      <RecentTaskList
        tasks={recentTasks}
        onTaskClick={openTaskDetail}
        onLoadMore={loadMoreTasks}
        hasMore={recentTasks.length > 0 && recentTasks.length % 50 === 0}
      />

      {/* 작업 상세 모달 */}
      <TaskDetailModal
        open={selectedTaskId !== null}
        onClose={() => { setSelectedTaskId(null); setTaskDetail(null); }}
        task={taskDetail}
      />
    </div>
  );
};

export default AIMonitoring;
